import React, { useState, useEffect, useLayoutEffect, useRef, useId } from 'react';
import {
  Drawer,
  Button,
  Tag,
  Divider,
  Typography,
  Input,
  Alert,
  Steps,
  Modal,
  Dropdown,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import {
  GlobalOutlined,
  LinkedinOutlined,
  ExportOutlined,
  SendOutlined,
  EditOutlined,
  PaperClipOutlined,
  ArrowLeftOutlined,
  DownOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  HourglassOutlined,
  UserSwitchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { BookmarkFilled, BookmarkOutlined } from '../icons/BookmarkIcon';
import { Opportunity, LLMRating, EmailTemplate } from '../../types';
import {
  approveOpportunity,
  getGmailStatus,
  getGmailAuthUrl,
  getDashboardStats,
  regenerateCoverLetter,
  getResumeAttachmentName,
  downloadOpportunityResume,
  markOpportunityViewed,
  getOpportunity,
  acceptContactProposal,
} from '../../services/api';
import { triggerBrowserDownload } from '../../utils/download';
import {
  ApiErrorCode,
  getApiErrorCode,
  getApiErrorMessage,
  GMAIL_AUTH_EXPIRED_EVENT,
} from '../../utils/errorUtils';
import { captureEvent } from '../../services/analytics';
import { clearGmailReturnTo } from '../../utils/gmailReturn';
import { AUTO_LANGUAGE } from '../../utils/languages';
import LanguagePicker from '../localization/LanguagePicker';
import DraftLanguageChip from '../localization/DraftLanguageChip';
import RejectModal from '../modals/RejectModal';
import WarmPathNudge from './WarmPathNudge';
import ContactCard from './ContactCard';
import DeclareApplicationModal from '../modals/DeclareApplicationModal';
import IntroRequestModal from '../modals/IntroRequestModal';
import ConnectGmailStep from '../email/ConnectGmailStep';
import { useGmailConsentPrimer } from '../../hooks/useGmailConsentPrimer';
import ReplyCaptureModal from '../email/ReplyCaptureModal';
import { renderBulletList } from '../../utils/renderUtils';
import { getPostingAge } from '../../utils/postingAge';
import {
  hasSeenReplyCaptureModal,
  isReplyCaptureModalRetired,
  markReplyCaptureModalSeen,
  retireReplyCaptureModal,
  THRESHOLD_COMPACT_REPROMPT,
  isVolumeReassuranceDismissed,
  dismissVolumeReassurance,
} from '../../utils/replyCaptureStorage';
import SourceTag from '../common/SourceTag';
import css from './OpportunityDrawer.module.scss';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

type DrawerStage = 'evaluate' | 'linkedin' | 'prepare' | 'review';

// Shown on the review screen until (and unless) the tailored filename resolves.
// Matches the generic name the server would fall back to, so a failed/slow preview
// fetch degrades to exactly what this screen showed before the feature existed.
const FALLBACK_RESUME_NAME = 'resume.pdf';

// Shown whenever the editor is reloaded because the opportunity's contact changed
// under it: a cover letter is written for one person, not for whoever replaced them.
const CONTACT_RELOADED_NOTICE =
  "This opportunity now has a different contact; we've reloaded the email.";

// Recipient verification (D16): the contact's address is being re-checked.
const CONTACT_CHECKING_TEXT =
  "We're confirming this contact's email. This usually takes a few minutes; we'll update this page when it's done.";

// What the live region says when the contact status changes under an open
// drawer. The panels carry the full story; these are short spoken versions.
const CONTACT_CONFIRMED_ANNOUNCEMENT =
  "This contact's email is confirmed. You can send your application.";
const CONTACT_PROPOSAL_ANNOUNCEMENT =
  "We couldn't confirm this contact's email. Another contact is suggested.";
const CONTACT_UNREACHABLE_ANNOUNCEMENT =
  'No reachable contact for this job, so we can’t send it for you.';

const fullName = (person: { firstName: string; lastName: string }) =>
  `${person.firstName} ${person.lastName}`.trim();

// What a cover-letter regeneration was asked for. Its answer is applied only if
// the drawer still shows the same opportunity, the draft is still bound to the
// same contact, and the editor hasn't been reloaded since. `reloadSeq` is the
// deciding check: every load of the editor bumps it, a contact rebind and an
// opportunity swap included, so the other two fields only make the intent
// explicit.
interface DraftToken {
  opportunityId: string;
  contactId: string | null;
  reloadSeq: number;
}

interface OpportunityDrawerProps {
  opportunity: Opportunity | null;
  open: boolean;
  onClose: () => void;
  onReject: () => void;
  onApply: () => void;
  // Fired after a successful (persisted) cover-letter regenerate so the parent
  // can keep its cached opportunity's template in sync (avoids a stale reopen).
  // `contactId` is the contact the letter was written for: the parent patches
  // only a record still bound to it. A record it has already switched to
  // another contact (queued in the same batch as this answer, before the
  // drawer could see it) keeps that contact's own letter.
  onRegenerated?: (opportunityId: string, template: EmailTemplate, contactId: string | null) => void;
  // Fired with a fresh copy of the opportunity (a refetch after a send or
  // regenerate was refused over its contact, or an accepted suggestion), so the
  // parent can replace its cached (and pinned) copy. Used only without
  // `loadFresh`.
  onRefreshed?: (opportunity: Opportunity) => void;
  // Runs a request that answers with a fresh copy of the opportunity (a
  // refetch, an accept) through the parent, which applies the copy to its own
  // (pinned) copy and resolves it — or resolves null when a newer copy
  // overtook the request (e.g. a list response or a contact event's refresh),
  // so the drawer must not reload from it either. Rejects when the request
  // fails. Without it, the drawer runs the request itself and hands the copy
  // to `onRefreshed`.
  loadFresh?: (request: () => Promise<Opportunity>) => Promise<Opportunity | null>;
  // Runs a write (an accept) through the parent, read-after-write: the write's
  // own answer (`written`) is never applied as the opportunity's state; once it
  // completes, the parent reads the opportunity back and applies that
  // (`fresh`: null when overtaken by something newer). `readError` is set when
  // that read failed — the write still went through. Rejects only when the
  // write fails. Without it, the drawer does the same itself and hands the read
  // copy to `onRefreshed`.
  loadAfterWrite?: (
    write: () => Promise<Opportunity>
  ) => Promise<{ written: Opportunity; fresh: Opportunity | null; readError?: unknown }>;
  // Fired when an UNREAD opportunity is opened (alongside the fire-and-forget
  // POST /view) so the parent can optimistically untint the row + drop the
  // tab badge without a refetch.
  onViewed?: (opportunityId: string) => void;
  // Save-for-later toggle. The parent owns the request, the optimistic fill and
  // the rollback (it also owns the row control, and both must agree), so this
  // drawer only reports the gesture and renders whatever `savedAt` comes back.
  onToggleSaved?: (opportunity: Opportunity) => void;
  // True while this opportunity's save/unsave is still in flight. The parent's
  // handler already drops a second click; this is the visible half of that
  // guard, so the control doesn't invite one it will silently discard.
  savePending?: boolean;
}

const ratingConfig: Record<LLMRating, { label: string; color: string }> = {
  perfect_fit: { label: 'Perfect Fit', color: '#52c41a' },
  strong_alignment: { label: 'Strong Alignment', color: '#73d13d' },
  potential_candidate: { label: 'Potential Candidate', color: '#faad14' },
  partial_fit: { label: 'Partial Fit', color: '#fa8c16' },
  misalignment: { label: 'Misalignment', color: '#ff4d4f' },
};

const OpportunityDrawer: React.FC<OpportunityDrawerProps> = ({
  opportunity,
  open,
  onClose,
  onReject,
  onApply,
  onRegenerated,
  onRefreshed,
  loadFresh,
  loadAfterWrite,
  onViewed,
  onToggleSaved,
  savePending,
}) => {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [declareModalOpen, setDeclareModalOpen] = useState(false);
  const [introModalOpen, setIntroModalOpen] = useState(false);
  const [stage, setStage] = useState<DrawerStage>('evaluate');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  // Local copy of the (mutable) cover-letter template. Seeded from the immutable
  // `opportunity` prop and updated on regenerate so the language chip reflects
  // the latest draft without waiting for the parent to refetch.
  const [template, setTemplate] = useState<EmailTemplate | null>(opportunity?.template ?? null);
  const [regenLanguage, setRegenLanguage] = useState<string>(AUTO_LANGUAGE);
  const [regenerating, setRegenerating] = useState(false);
  // Tracks the currently-shown opportunity id so an in-flight regenerate for a
  // previous opportunity can't write its result into a swapped-in one.
  const opportunityIdRef = useRef<string | undefined>(opportunity?.id);
  // The contact the editor's text was loaded for. The send carries THIS id, not
  // the prop's current contact, so the server's contact guard checks the
  // contact the sent text was written for. It is state, set in the same batch as
  // subject/body/footer, so the send handler reads text and id from the same
  // render; the ref mirrors it for checks made outside a render (the
  // regeneration token, the contact-change effect).
  const [draftContactId, setDraftContactId] = useState<string | null>(
    opportunity?.contact?.id ?? null
  );
  const draftContactIdRef = useRef<string | null>(draftContactId);
  // Bumped on every (re)load of the editor, so an in-flight regeneration that
  // predates a reload is dropped even when the contact is unchanged.
  const reloadSeqRef = useRef(0);
  // Inline information shown above the editor and the review (a reload after a
  // contact change, or why a send didn't go out). Cleared on every fresh open.
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // In flight: "Use this contact" (accepting a suggested contact).
  const [accepting, setAccepting] = useState(false);
  // Where focus goes once an accept has settled: the panel the user pressed is
  // about to disappear (success) or be replaced (409), and focus must not be
  // dropped onto the page. `seq` makes a repeat request a new value.
  const [focusRequest, setFocusRequest] = useState<
    { target: 'accepted' | 'notice'; seq: number } | null
  >(null);
  const subjectInputRef = useRef<InputRef>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  // Spoken through a live region that is always mounted, so a status that
  // changes while the drawer is open is announced reliably (a region that
  // mounts together with its text often isn't).
  // `key` is the opportunity it speaks for: the text is rendered in a node keyed
  // by it, so moving between two opportunities with the same status inserts a
  // new node (announced) instead of leaving identical text in place (silent).
  const [announcement, setAnnouncementState] = useState<{ key: string; text: string }>({
    key: '',
    text: '',
  });
  const announcedRef = useRef<{ id?: string; status: string; contactId: string | null }>({
    status: 'ok',
    contactId: null,
  });
  // Ties each status panel to its visible title (aria-labelledby).
  const statusTitleId = useId();
  const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);
  // null until the status loads; false = connected but Google's "Send email on
  // your behalf" box wasn't checked, so the backend can't send for this user.
  const [gmailCanSend, setGmailCanSend] = useState<boolean | null>(null);
  // Tailored filename the resume will be attached as, previewed on the review
  // screen. Defaults to the generic fallback so any fetch failure looks unchanged.
  const [resumeAttachmentName, setResumeAttachmentName] = useState<string>(FALLBACK_RESUME_NAME);
  // Busy flag for the independent-apply resume download. The ref is the
  // re-entry gate: it's read at call time, so it can't be defeated by a stale
  // closure (e.g. the dropdown overlay's captured onClick) or a double click
  // that lands before the disabled/loading re-render; the state just drives UI.
  const [downloadingResume, setDownloadingResume] = useState(false);
  const downloadingResumeRef = useRef(false);
  const [applicationCountBeforeSubmit, setApplicationCountBeforeSubmit] = useState<number | null>(null);
  const [replyCaptureModal, setReplyCaptureModal] = useState<
    { open: boolean; variant: 'full' | 'compact'; count: number }
  >({ open: false, variant: 'full', count: 0 });
  // Dismissible "volume wins — keep applying" reassurance shown at the evaluate stage.
  const [reassuranceDismissed, setReassuranceDismissed] = useState(() =>
    isVolumeReassuranceDismissed()
  );

  // Loads the editor from an opportunity's stored template and binds the draft
  // to its contact. Any regeneration still in flight is invalidated.
  function loadDraft(source: Opportunity) {
    const next = source.template ?? null;
    setSubject(next?.subject ?? '');
    setBody(next?.body ?? '');
    setFooter(next?.footer ?? '');
    setTemplate(next);
    const contactId = source.contact?.id ?? null;
    setDraftContactId(contactId);
    draftContactIdRef.current = contactId;
    reloadSeqRef.current += 1;
    setRegenerating(false);
  }

  // Whether `source` carries a different contact from the one the draft is
  // bound to. A contact that has gone null is NOT a switch: recipient
  // verification never clears it (an unreachable opportunity keeps its contact,
  // D18), so a null is a partial payload rather than a new person, and there is
  // no letter to load for nobody. The draft keeps its binding, and if the server
  // really has no contact any more it refuses the send.
  function isContactSwitch(source: Opportunity) {
    const next = source.contact?.id ?? null;
    return next !== null && next !== draftContactIdRef.current;
  }

  // A reload forced by a contact change. Review drops back to the editor: the
  // letter under the Confirm & Send button has just been replaced, and it
  // should be read before it is sent.
  function reloadForNewContact(source: Opportunity) {
    loadDraft(source);
    setNotice(CONTACT_RELOADED_NOTICE);
    setStage((current) => (current === 'review' ? 'prepare' : current));
  }

  // Runs a request answering with a fresh copy and hands the copy to the
  // parent: null when a newer copy overtook it (see `loadFresh`).
  async function requestFresh(request: () => Promise<Opportunity>): Promise<Opportunity | null> {
    if (loadFresh) return loadFresh(request);
    const fresh = await request();
    onRefreshed?.(fresh);
    return fresh;
  }

  // A write, then the opportunity read back (see `loadAfterWrite`).
  async function writeThenRead(
    write: () => Promise<Opportunity>
  ): Promise<{ written: Opportunity; fresh: Opportunity | null; readError?: unknown }> {
    if (loadAfterWrite) return loadAfterWrite(write);
    const written = await write();
    try {
      return { written, fresh: await requestFresh(() => getOpportunity(written.id)) };
    } catch (readError) {
      return { written, fresh: null, readError };
    }
  }

  // Fetch the opportunity again and hand it to the parent. Returns null (after
  // saying why) when the fetch fails, when a newer copy overtook it, or when
  // the drawer has moved on to another opportunity in the meantime.
  async function refetchOpportunity(opportunityId: string): Promise<Opportunity | null> {
    try {
      const fresh = await requestFresh(() => getOpportunity(opportunityId));
      return fresh && opportunityIdRef.current === opportunityId ? fresh : null;
    } catch (error) {
      if (opportunityIdRef.current === opportunityId) message.error(getApiErrorMessage(error));
      return null;
    }
  }

  // The server refused because the opportunity's contact is no longer the one
  // this draft was written for. Refresh the opportunity, and reload the draft if
  // the contact really did change — whatever the parent does with the fresh copy
  // (its re-render then finds the draft already bound and is a no-op). If the
  // refetch shows the draft's own contact again, the draft is still right; keep it.
  async function reloadAfterContactChanged(opportunityId: string) {
    const fresh = await refetchOpportunity(opportunityId);
    if (fresh && isContactSwitch(fresh)) reloadForNewContact(fresh);
  }

  // Tracks which opportunity the editor is currently seeded from, so the seed
  // runs once per open per id. Cleared on close, so a close+reopen (even of the
  // same row) always re-seeds from scratch.
  const seededOpportunityIdRef = useRef<string | undefined>(undefined);

  // The one exception to "same id, leave the editor alone": a refetch (an SSE
  // refresh, or the reload after a refused send) that shows a DIFFERENT contact.
  // The draft was written for the old one, so keeping it would either send one
  // person's letter to another or be refused by the server — reload it from the
  // new contact's template and say so.
  //
  // A layout effect, so the reload commits before the browser paints: no frame
  // ever shows (or offers to send) the old letter beside the new contact.
  // Declared BEFORE the seed effect below, so it runs first in a commit and
  // leaves a new or reopened opportunity to the seed — the seeded-id check is
  // what defers (the seed hasn't recorded the new id yet).
  useLayoutEffect(() => {
    if (!open || !opportunity?.id) return;
    if (seededOpportunityIdRef.current !== opportunity.id) return;
    if (!isContactSwitch(opportunity)) return;
    reloadForNewContact(opportunity);
  }, [open, opportunity?.id, opportunity?.contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-seed the editor + reset the stage only when the drawer opens, or when a
  // DIFFERENT opportunity is shown (keyed on id, not object identity) — so
  // patching the same opportunity's template in place after a regenerate
  // doesn't yank the user out of the editor.
  //
  // A transient drop-out of the prop must NEVER clear these fields: opening the
  // drawer stamps viewed_at, so a refetch can reorder the row off the current
  // page and momentarily leave the parent's lookup empty. subject/body/footer
  // hold unsaved cover-letter edits with no autosave — the Regenerate path puts
  // up a blocking "Replace your edits?" confirm before touching them, so a
  // refetch certainly must not wipe them silently.
  //
  // A layout effect for the same reason as the contact reset above: switching
  // opportunity A → B must not paint B's header over A's letter for a frame. If
  // A and B share a contact, a send from that frame would carry a contact id the
  // server accepts — A's letter would go out for job B.
  useLayoutEffect(() => {
    if (!open) {
      seededOpportunityIdRef.current = undefined;
      return;
    }
    if (!opportunity?.id) return;
    if (seededOpportunityIdRef.current === opportunity.id) return;
    seededOpportunityIdRef.current = opportunity.id;
    opportunityIdRef.current = opportunity.id;
    loadDraft(opportunity);
    setRegenLanguage(AUTO_LANGUAGE);
    setStage('evaluate');
    setNotice(null);
    // An accept still in flight for the previous opportunity no longer clears
    // its own spinner (it checks the id), so it mustn't carry over.
    setAccepting(false);
    setDeclareModalOpen(false);
    setIntroModalOpen(false);
  }, [open, opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Runs after the commit that settled an accept, so the stage it reads is the
  // one on screen: the new letter's subject on Prepare (Review has just dropped
  // back to it), otherwise the opportunity heading; a 409 goes to the notice.
  useEffect(() => {
    if (!focusRequest) return;
    if (focusRequest.target === 'notice') noticeRef.current?.focus();
    else if (stage === 'prepare') subjectInputRef.current?.focus();
    else headingRef.current?.focus();
  }, [focusRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feeds the live region. A freshly shown opportunity announces only a check
  // in progress (its panel says the rest); a change on the one already shown
  // announces the new status, and a check that came back fine for the same
  // contact says so. A different contact is the reload notice's to explain.
  useEffect(() => {
    // Closed: forget what was said, so a reopen (even of the same row) starts
    // fresh, and leave nothing behind in the hidden region.
    if (!open) {
      announcedRef.current = { status: 'ok', contactId: null };
      setAnnouncementState({ key: '', text: '' });
      return;
    }
    if (!opportunity) return;
    const opportunityId = opportunity.id;
    const setAnnouncement = (text: string) => setAnnouncementState({ key: opportunityId, text });
    const status = opportunity.contactStatus ?? 'ok';
    const contactId = opportunity.contact?.id ?? null;
    const prev = announcedRef.current;
    announcedRef.current = { id: opportunity.id, status, contactId };
    const sameOpportunity = prev.id === opportunity.id;
    if (sameOpportunity && prev.status === status) return;
    if (status === 'checking') setAnnouncement(CONTACT_CHECKING_TEXT);
    else if (!sameOpportunity) setAnnouncement('');
    else if (status === 'proposal') setAnnouncement(CONTACT_PROPOSAL_ANNOUNCEMENT);
    else if (status === 'unreachable') setAnnouncement(CONTACT_UNREACHABLE_ANNOUNCEMENT);
    else if (prev.status === 'checking' && prev.contactId === contactId)
      setAnnouncement(CONTACT_CONFIRMED_ANNOUNCEMENT);
    else setAnnouncement('');
  }, [open, opportunity?.id, opportunity?.contactStatus, opportunity?.contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check Gmail status when drawer opens
  useEffect(() => {
    if (open) {
      getGmailStatus()
        .then((status) => {
          setGmailConnected(status.connected);
          setGmailCanSend(status.canSend !== false);
        })
        .catch(() => {
          setGmailConnected(false);
          setGmailCanSend(null);
        });
    }
  }, [open]);

  // Preview the tailored resume-attachment filename when the drawer opens (the
  // review screen shows it two stages later, so it's ready by then). Reset to the
  // fallback first so a swapped-in opportunity never shows the previous name; keep
  // the fallback on error or a null result rather than surfacing a failure.
  useEffect(() => {
    if (!open || !opportunity?.id) return;
    const opportunityId = opportunity.id;
    setResumeAttachmentName(FALLBACK_RESUME_NAME);
    getResumeAttachmentName(opportunityId)
      .then((res) => {
        // Ignore a late response for an opportunity the drawer has since swapped away from.
        if (opportunityIdRef.current === opportunityId && res.resumeAttachmentName) {
          setResumeAttachmentName(res.resumeAttachmentName);
        }
      })
      .catch(() => {
        /* keep the resume.pdf fallback on a transient error */
      });
  }, [open, opportunity?.id]);

  // Fetch application count when drawer opens — used to detect first / threshold sends.
  useEffect(() => {
    if (open) {
      getDashboardStats()
        .then((stats) => setApplicationCountBeforeSubmit(stats.totalApplications))
        .catch(() => setApplicationCountBeforeSubmit(null));
    }
  }, [open]);

  // Mark unread opportunities read on open (email-style inbox, design §14).
  // Fire-and-forget: the drawer never waits on it, and a failure only means the
  // row stays tinted until the next refetch — log and move on. The optimistic
  // onViewed callback untints the row immediately. Deliberately NOT keyed on
  // viewedAt: the parent's optimistic patch flips it mid-open and must not
  // re-trigger this effect.
  useEffect(() => {
    if (!open || !opportunity || opportunity.viewedAt != null) return;
    markOpportunityViewed(opportunity.id).catch((error) => {
      console.error('Mark opportunity viewed error:', error);
    });
    onViewed?.(opportunity.id);
  }, [open, opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- viewedAt is deliberately omitted: the parent's optimistic patch flips it mid-open and would re-fire this effect.

  // Fire on open: 'opportunity_viewed' (previously defined but never fired) powers
  // hesitater detection — viewed the decision-maker, didn't apply. Also log when the
  // reassurance is surfaced, so its effect on send-through is measurable.
  useEffect(() => {
    if (!open || !opportunity) return;
    captureEvent('opportunity_viewed', {
      opportunity_id: opportunity.id,
      llm_rating: opportunity.llmRating,
      search_id: opportunity.searchId,
    });
    if (!reassuranceDismissed && opportunity.contactStatus !== 'unreachable') {
      captureEvent('apply_reassurance_shown', { opportunity_id: opportunity.id });
    }
  }, [open, opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check Gmail status when user returns to tab (after OAuth in new tab).
  // Armed while connecting is still blocked: not connected at all, or connected
  // without send permission (the "Fix permission" round trip).
  useEffect(() => {
    if (!open || (gmailConnected !== false && gmailCanSend !== false)) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        getGmailStatus()
          .then((status) => {
            if (!status.connected) return; // still not connected — keep waiting
            const sendEnabled = status.canSend !== false;
            setGmailConnected(true);
            setGmailCanSend(sendEnabled);
            if (sendEnabled) {
              message.success(gmailConnected === false ? 'Gmail connected!' : 'Sending enabled!');
            }
          })
          .catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [open, gmailConnected, gmailCanSend]);

  // Drop connected state when the backend signals an expired Gmail token.
  useEffect(() => {
    const onExpired = () => setGmailConnected(false);
    window.addEventListener(GMAIL_AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(GMAIL_AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const handleConnectGmail = async () => {
    // Safari's popup blocker only allows window.open synchronously inside the
    // user gesture; open the tab first and point it at the auth URL after the
    // fetch resolves.
    const authTab = window.open('', '_blank');
    try {
      // The new tab inherits a copy of this tab's sessionStorage; clear any
      // stored origin so it doesn't relay the new tab away from Settings.
      clearGmailReturnTo();
      const { authUrl } = await getGmailAuthUrl();
      if (authTab) {
        authTab.location.href = authUrl;
      } else {
        window.open(authUrl, '_blank');
      }
    } catch (error) {
      authTab?.close();
      message.error(getApiErrorMessage(error));
    }
  };

  // Every connect/fix trigger in the drawer opens the consent primer first;
  // Continue runs the untouched new-tab flow above (its window.open stays
  // inside the Continue click's user gesture, so popup blockers allow it).
  const { openPrimer, primer } = useGmailConsentPrimer(handleConnectGmail);

  if (!opportunity) return null;

  const rating = ratingConfig[opportunity.llmRating];
  const age = getPostingAge(opportunity.jobPostingDate);
  // Remote-search eligibility heads-up. Only `likely` and `unstated` get one:
  // `eligible` needs none, and an `excluded` ad is filed as a filtered row and
  // never becomes an opportunity — that sentence belongs to the filtered
  // drawer, which also holds the workplace type it turns on. Any other value
  // (null on a local search, a grade this build doesn't know) says nothing
  // rather than fall through to copy that doesn't fit it.
  const eligibilityNote =
    opportunity.eligibility === 'likely' || opportunity.eligibility === 'unstated'
      ? opportunity.eligibility
      : null;
  // Blank reads as absent, same rule as the search card's remote badge.
  const residence = opportunity.search.residence?.trim();
  const eligibilityQuote = opportunity.eligibilityQuote?.trim();
  // Read straight off the prop, not local state: the parent applies the
  // optimistic fill and the rollback, so mirroring it here would let the two
  // controls disagree after a failed request.
  const saved = opportunity.savedAt != null;
  const gmailNotConnected = gmailConnected === false;
  // Connected, but the "Send email on your behalf" box wasn't checked — the
  // account is linked and still can't send; treat it as blocking like
  // not-connected, with re-consent ('fix') copy instead of connect copy.
  const gmailNoSend = gmailConnected === true && gmailCanSend === false;
  const gmailBlocked = gmailNotConnected || gmailNoSend;
  // Recipient verification (D16–D18). Absent/null is a legacy row or an older
  // backend and reads as `ok`. While the address is being checked, has a
  // suggested replacement, or nobody is reachable, nothing can be sent; the
  // server refuses it anyway, and the button says so first. Writing the letter
  // is only pointless for the last two — while the check runs the contact may
  // well turn out fine. A status this build doesn't know blocks nothing: the
  // server's gate still decides.
  const contactStatus = opportunity.contactStatus ?? 'ok';
  const writeBlocked = contactStatus === 'proposal' || contactStatus === 'unreachable';
  const sendBlocked = writeBlocked || contactStatus === 'checking';

  const handleClose = () => {
    setStage('evaluate');
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setFooter(template.footer);
    }
    onClose();
  };

  const handleReset = () => {
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setFooter(template.footer);
      message.info('Reverted to the current draft');
    }
  };

  // Whether the editor has unsaved edits relative to the loaded/regenerated draft.
  const isCoverLetterDirty = () =>
    !!template &&
    (subject !== template.subject || body !== template.body || footer !== template.footer);

  const isCurrentDraft = (token: DraftToken) =>
    opportunityIdRef.current === token.opportunityId &&
    draftContactIdRef.current === token.contactId &&
    reloadSeqRef.current === token.reloadSeq;

  const runRegenerate = async () => {
    if (!opportunity) return;
    const reqId = opportunity.id;
    const token: DraftToken = {
      opportunityId: reqId,
      contactId: draftContactIdRef.current,
      reloadSeq: reloadSeqRef.current,
    };
    setRegenerating(true);
    try {
      // "auto" defers to the search preference / capability gate; a forced code
      // is honored as-is. Regenerate PERSISTS — it overwrites the stored template.
      const next = await regenerateCoverLetter(
        reqId,
        regenLanguage === AUTO_LANGUAGE ? undefined : regenLanguage
      );
      // Bail if the drawer swapped to a different opportunity, the draft was
      // rebound to another contact, or the editor was reloaded mid-request.
      if (!isCurrentDraft(token)) return;
      // The server writes the letter for the opportunity's CURRENT contact,
      // which may not be the one this editor is bound to (a switch the browser
      // hasn't heard of yet). Read the opportunity back — its contact and its
      // stored letter arrive together — and take the letter from there. The
      // letter is never shown or cached without that read: its own answer
      // doesn't say whom it was written for.
      let canonical: Opportunity | null;
      try {
        canonical = await requestFresh(() => getOpportunity(reqId));
      } catch (readError) {
        if (!isCurrentDraft(token)) return;
        message.error(
          `Your letter was regenerated, but couldn't be loaded: ${getApiErrorMessage(readError)} Reopen this job to see it.`
        );
        return;
      }
      if (!isCurrentDraft(token)) return;
      if (!canonical) {
        // A newer copy overtook the read; it reaches the drawer on its own.
        message.warning("Your letter was regenerated, but couldn't be loaded here. Reopen this job to see it.");
        return;
      }
      if (isContactSwitch(canonical)) {
        // Written for the new contact: bind it to them, and say so.
        reloadForNewContact(canonical);
      } else {
        const letter = canonical.template ?? next;
        setTemplate(letter);
        setSubject(letter.subject);
        setBody(letter.body);
        setFooter(letter.footer);
        // Keep the parent's cached opportunity in sync (regenerate persisted), so
        // reopening the drawer shows the regenerated draft instead of a stale one.
        onRegenerated?.(reqId, letter, token.contactId);
      }
      captureEvent('cover_letter_regenerated', {
        language: next.language,
        is_auto_language: regenLanguage === AUTO_LANGUAGE,
      });
      message.success(
        next.languageName
          ? `Cover letter regenerated in ${next.languageName}`
          : 'Cover letter regenerated'
      );
    } catch (error) {
      if (!isCurrentDraft(token)) return;
      if (getApiErrorCode(error) === ApiErrorCode.ContactChanged) {
        // The contact changed while the letter was being written; the server
        // kept nothing. Load the current contact and its letter instead.
        await reloadAfterContactChanged(reqId);
        return;
      }
      message.error(getApiErrorMessage(error));
    } finally {
      // A superseded request must not clear the spinner of a newer one (the
      // reload already cleared this one's).
      if (isCurrentDraft(token)) setRegenerating(false);
    }
  };

  const handleRegenerate = () => {
    if (isCoverLetterDirty()) {
      Modal.confirm({
        title: 'Replace your edits?',
        content:
          'Regenerating writes a fresh cover letter and discards your current edits. This overwrites the saved draft. Continue?',
        okText: 'Regenerate',
        cancelText: 'Keep my edits',
        onOk: runRegenerate,
      });
    } else {
      runRegenerate();
    }
  };

  // Download the exact resume outreach would send — for applying on your own
  // (the "View posting" → apply-directly → "I just applied" path).
  const handleDownloadResume = async (format: 'pdf' | 'docx') => {
    if (!opportunity || downloadingResumeRef.current) return;
    const reqId = opportunity.id;
    downloadingResumeRef.current = true;
    setDownloadingResume(true);
    try {
      // No silent format fallback: the docxAvailable flag gates the DOCX option
      // up front, so a 404 here is a genuine error and surfaces as one below.
      const download = await downloadOpportunityResume(reqId, format);
      triggerBrowserDownload(download.blob, download.filename);
      // Record what was actually delivered — the served filename is the source
      // of truth (the PDF path honestly serves a legacy .docx upload as .docx).
      const delivered: 'pdf' | 'docx' = download.filename.toLowerCase().endsWith('.docx')
        ? 'docx'
        : 'pdf';
      captureEvent('opportunity_resume_downloaded', {
        format: delivered,
        requested_format: format,
        opportunity_id: reqId,
      });
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      downloadingResumeRef.current = false;
      setDownloadingResume(false);
    }
  };

  const handleSubmit = async () => {
    if (!opportunity) return;
    const reqId = opportunity.id;
    setLoading(true);
    try {
      await approveOpportunity(reqId, {
        sendEmail: true,
        emailSubject: subject,
        emailBody: body,
        emailFooter: footer,
        // The render's STATE value, not draftContactIdRef: the ref can already
        // hold a newer contact than the text this render captured above.
        contactId: draftContactId ?? undefined,
      });
      message.success('Application submitted and email sent!');

      // North Star: a submitted application. Capture before handleClose() resets the editor.
      captureEvent('application_submitted', {
        llm_rating: opportunity.llmRating,
        email_customized: isCoverLetterDirty(),
        send_email: true,
      });

      const priorCount = applicationCountBeforeSubmit;
      const newCount = priorCount !== null ? priorCount + 1 : null;

      handleClose();
      onApply();

      // Trigger reply-capture modal at first send (full) or threshold send (compact).
      const shouldShowFull = newCount === 1 && !hasSeenReplyCaptureModal();
      const shouldShowCompact =
        newCount === THRESHOLD_COMPACT_REPROMPT && !isReplyCaptureModalRetired();

      if (shouldShowFull && newCount !== null) {
        setTimeout(() => {
          markReplyCaptureModalSeen();
          setReplyCaptureModal({ open: true, variant: 'full', count: newCount });
        }, 1500);
      } else if (shouldShowCompact && newCount !== null) {
        setTimeout(() => {
          setReplyCaptureModal({ open: true, variant: 'compact', count: newCount });
        }, 1500);
      }
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === ApiErrorCode.ContactNotSendable || code === ApiErrorCode.ContactCheckPending) {
        // Nothing was sent, and the opportunity's contact status moved on the
        // server: say why (the server's own words) and fetch the new state.
        // The draft is kept while it is bound to the same contact; if the
        // refetch already shows a different one, it is reloaded instead.
        if (opportunityIdRef.current === reqId) setNotice(getApiErrorMessage(error));
        const fresh = await refetchOpportunity(reqId);
        if (fresh && isContactSwitch(fresh)) reloadForNewContact(fresh);
      } else if (code === ApiErrorCode.ContactChanged) {
        await reloadAfterContactChanged(reqId);
      } else {
        message.error(getApiErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  // "Use this contact": the server swaps in the suggested contact and writes
  // their letter, answering with the updated opportunity. That copy goes to the
  // parent (it replaces the pinned one) and the editor is loaded from it here,
  // so the draft is bound to the new contact before the parent's re-render
  // reaches the contact-change effect — which then finds nothing to do. The
  // user asked for this switch, so it says so instead of the reload notice.
  //
  // The accept publishes a contact event before its answer returns, so the
  // page's refresh can get there first: the new contact and their letter are
  // already loaded, and the user may be editing it. Then the answer is no
  // newer than what is on screen — the parent drops it (null) and the editor,
  // which has moved on since the accept started, is left alone.
  const runAcceptProposal = async () => {
    if (!opportunity) return;
    const reqId = opportunity.id;
    const token: DraftToken = {
      opportunityId: reqId,
      contactId: draftContactIdRef.current,
      reloadSeq: reloadSeqRef.current,
    };
    // The suggestion on screen now, named in the request: if it has changed by
    // the time the server sees it, the server refuses (409) rather than accept
    // a contact the user never saw.
    const proposed = opportunity.proposedContact ?? null;
    if (!proposed) return;
    setAccepting(true);
    try {
      const { written, fresh, readError } = await writeThenRead(
        () => acceptContactProposal(reqId, proposed.id)
      );
      if (opportunityIdRef.current !== reqId) return;
      // The accepted contact, for the message only: the editor and the cached
      // copies take the opportunity as read back after the accept.
      const accepted = written.contact ?? proposed;
      if (fresh && isCurrentDraft(token)) {
        // Something may have moved the opportunity on past the accepted
        // contact already; then it is a switch like any other, and says so.
        if ((fresh.contact?.id ?? null) === accepted.id) loadDraft(fresh);
        else reloadForNewContact(fresh);
        // A letter that was on Review a moment ago has just been replaced.
        setStage((current) => (current === 'review' ? 'prepare' : current));
      }
      const boundToAccepted = draftContactIdRef.current === accepted.id;
      // The reload notice a refresh may have put up is explained by the
      // user's own accept; anything else (a later switch) stays said.
      if (boundToAccepted) setNotice(null);
      if (readError) {
        message.error(
          `${fullName(accepted)} is now the contact for this job, but the new email couldn't be loaded: ${getApiErrorMessage(readError)}`
        );
      } else {
        message.success(`${fullName(accepted)} is now the contact for this job.`);
      }
      if (boundToAccepted) {
        setFocusRequest((prev) => ({ target: 'accepted', seq: (prev?.seq ?? 0) + 1 }));
      }
    } catch (error: any) {
      if (opportunityIdRef.current !== reqId) return;
      if (error?.response?.status === 409) {
        // The suggestion changed (or the opportunity moved on) since it was
        // shown. Say so in the server's words and show the current state.
        setNotice(getApiErrorMessage(error));
        const fresh = await refetchOpportunity(reqId);
        if (fresh && isContactSwitch(fresh)) reloadForNewContact(fresh);
        if (opportunityIdRef.current === reqId) {
          setFocusRequest((prev) => ({ target: 'notice', seq: (prev?.seq ?? 0) + 1 }));
        }
      } else {
        message.error(getApiErrorMessage(error));
      }
    } finally {
      if (opportunityIdRef.current === reqId) setAccepting(false);
    }
  };

  // Accepting replaces the letter, so edits get the same blocking question
  // Regenerate asks. They were written for the old contact and can't be sent
  // to them either way, but they may be worth copying first.
  const handleAcceptProposal = () => {
    if (!isCoverLetterDirty()) {
      runAcceptProposal();
      return;
    }
    const proposed = opportunity?.proposedContact;
    Modal.confirm({
      title: 'Replace your edits?',
      content: `Using ${
        proposed ? fullName(proposed) : 'the suggested contact'
      } replaces your edited email with a new one written for them.`,
      okText: 'Replace my edits',
      cancelText: 'Keep my edits',
      // The accept moves focus itself when it settles (see focusRequest); the
      // modal handing it back to "Use this contact" on close would undo that
      // — on a 409, pulling it off the notice that explains the refusal.
      focusTriggerAfterClose: false,
      onOk: runAcceptProposal,
    });
  };

  const openDeclare = () => {
    captureEvent('declare_application_opened', { opportunity_id: opportunity.id });
    setDeclareModalOpen(true);
  };

  const closeReplyCaptureModal = () => {
    if (replyCaptureModal.variant === 'compact') {
      retireReplyCaptureModal();
    } else {
      markReplyCaptureModalSeen();
    }
    setReplyCaptureModal({ ...replyCaptureModal, open: false });
  };

  const handleReplyCaptureForward = () => {
    window.open('https://www.jobbeast.org/email-integration/manual-forward', '_blank', 'noopener');
    closeReplyCaptureModal();
  };

  const handleReplyCaptureInstall = () => {
    closeReplyCaptureModal();
  };

  const handleDismissReassurance = () => {
    dismissVolumeReassurance();
    setReassuranceDismissed(true);
    if (opportunity) {
      captureEvent('apply_reassurance_dismissed', { opportunity_id: opportunity.id });
    }
  };

  // Reframes the apply decision: the filtering is done; reaching a real person is the
  // edge, and volume — not any single send — is what lands the interview.
  const renderVolumeReassurance = () => (
    <Alert
      type="info"
      showIcon
      closable
      onClose={handleDismissReassurance}
      icon={<RiseOutlined />}
      className={css.volumeAlert}
      message="Contacting a real person beats applying online"
      description={
        <>
          We've filtered this role and found the right person to reach — not a portal.
          <br />
          The measured unlock is a real vouch: a genuinely endorsed referral can be{' '}
          <strong>up to ~13× more likely</strong> to land an interview.
          <br />
          It's a numbers game: most people send <strong>10–20</strong>, sometimes more, before
          their first interview.
        </>
      }
    />
  );

  // Kept in its slot until dismissed or the drawer re-seeds: a reload can land
  // while the user is reading, and a toast would be gone before it was seen.
  // The wrapper takes focus when an accept is refused (see focusRequest).
  const renderNotice = () =>
    notice && (
      <div ref={noticeRef} tabIndex={-1} className={css.focusTarget}>
        <Alert
          // Remount per message, so a dismissed notice can't keep a later one hidden.
          key={notice}
          type="info"
          showIcon
          closable
          onClose={() => setNotice(null)}
          message={notice}
          className={css.alert}
        />
      </div>
    );

  // The posting itself, for applying without us. A real link (new tab), not a
  // button that opens one, so it behaves like every other link to the ad.
  const renderJobSiteLink = (primary: boolean) =>
    opportunity.jobPostingUrl ? (
      <Button
        type={primary ? 'primary' : 'default'}
        href={opportunity.jobPostingUrl}
        target="_blank"
        rel="noopener noreferrer"
        icon={<ExportOutlined aria-hidden />}
        aria-label="Apply on the job site (opens in a new tab)"
      >
        Apply on the job site
      </Button>
    ) : null;

  // Recipient verification (D16–D18): what the contact check means for this
  // opportunity, shown at the top of every stage so a status that changes while
  // the user is writing or reviewing is seen where they are. One shape, three
  // tones: amber while we wait, green when a person is suggested, neutral grey
  // when nobody is reachable — a dead end for email, not an error of theirs.
  const renderContactStatus = () => {
    if (contactStatus === 'checking') {
      return (
        // Not a live region itself: the always-mounted one below speaks it.
        <div className={`${css.contactStatus} ${css.contactStatusChecking}`}>
          <HourglassOutlined aria-hidden className={css.contactStatusIcon} />
          <div className={css.contactStatusBody}>
            <p className={css.contactStatusText}>{CONTACT_CHECKING_TEXT}</p>
          </div>
        </div>
      );
    }

    if (contactStatus === 'proposal') {
      const proposed = opportunity.proposedContact ?? null;
      return (
        <section
          className={`${css.contactStatus} ${css.contactStatusProposal}`}
          aria-labelledby={statusTitleId}
        >
          <UserSwitchOutlined aria-hidden className={css.contactStatusIcon} />
          <div className={css.contactStatusBody}>
            <p className={css.contactStatusTitle} id={statusTitleId}>
              We couldn't confirm{' '}
              {opportunity.contact ? `${fullName(opportunity.contact)}'s` : "this contact's"} email.
            </p>
            {proposed && (
              <div className={css.suggestedContact}>
                <span>
                  Suggested contact: <strong className={css.suggestedName}>{fullName(proposed)}</strong>
                </span>
                {proposed.position && (
                  <span className={css.suggestedPosition}>{proposed.position}</span>
                )}
              </div>
            )}
            {proposed && (
              <p className={css.contactStatusText}>
                We'll write a new email to {proposed.firstName} for you to read. Nothing is sent
                until you press Confirm & Send.
              </p>
            )}
            <div className={css.contactStatusActions}>
              <Button
                type="primary"
                onClick={handleAcceptProposal}
                loading={accepting}
                disabled={!proposed}
              >
                Use this contact
              </Button>
              {renderJobSiteLink(false)}
              <Button onClick={() => setRejectModalOpen(true)} disabled={accepting}>
                Dismiss
              </Button>
            </div>
          </div>
        </section>
      );
    }

    if (contactStatus === 'unreachable') {
      return (
        <section
          className={`${css.contactStatus} ${css.contactStatusUnreachable}`}
          aria-labelledby={statusTitleId}
        >
          <StopOutlined aria-hidden className={css.contactStatusIcon} />
          <div className={css.contactStatusBody}>
            <p className={css.contactStatusTitle} id={statusTitleId}>
              No reachable contact
            </p>
            <p className={css.contactStatusText}>
              We couldn't confirm an email address for anyone at {opportunity.company.name}, so
              we can't send this application for you.{' '}
              {opportunity.jobPostingUrl
                ? 'You can still apply on the job site, then let us know so we can track it.'
                : 'If you apply another way, let us know so we can track it.'}
            </p>
            <div className={css.contactStatusActions}>
              {renderJobSiteLink(true)}
              <Button onClick={openDeclare}>I applied directly</Button>
              <Button onClick={() => setRejectModalOpen(true)}>Dismiss</Button>
            </div>
          </div>
        </section>
      );
    }

    return null;
  };

  const renderGmailAlert = (type: 'warning' | 'info') =>
    gmailNoSend ? (
      <Alert
        message="Sending isn't enabled"
        description={
          <div className={css.gmailAlert}>
            <span>Gmail is connected, but sending isn't enabled — we can't send applications yet.</span>
            <Button
              size="small"
              type="primary"
              onClick={() => openPrimer('fix')}
              className={css.gmailConnectButton}
            >
              Fix permission
            </Button>
          </div>
        }
        type="warning"
        showIcon
        className={css.alert}
      />
    ) : (
      <Alert
        message="Gmail Not Connected"
        description={
          <div className={css.gmailAlert}>
            <span>Connect your Gmail to send applications from your email address.</span>
            <Button
              size="small"
              type="primary"
              onClick={() => openPrimer('connect')}
              className={css.gmailConnectButton}
            >
              Connect Gmail
            </Button>
          </div>
        }
        type={type}
        showIcon
        className={css.alert}
      />
    );

  // Honest download control (design §3b), shared by the evaluate and linkedin
  // stages: the PDF/DOCX dropdown is offered only when tailoring actually
  // produced a DOCX (backend docxAvailable); otherwise a single, explicit
  // PDF button — no format menu promising what we can't deliver.
  const renderResumeDownloadButton = () =>
    opportunity.docxAvailable ? (
      <Dropdown
        trigger={['click']}
        disabled={downloadingResume}
        menu={{
          items: [
            { key: 'pdf', label: 'PDF' },
            { key: 'docx', label: 'DOCX (editable)' },
          ],
          onClick: ({ key }) => handleDownloadResume(key as 'pdf' | 'docx'),
        }}
      >
        <Button icon={<DownloadOutlined />} loading={downloadingResume}>
          Download resume <DownOutlined className={css.downloadResumeChevron} />
        </Button>
      </Dropdown>
    ) : (
      <Button
        icon={<DownloadOutlined />}
        loading={downloadingResume}
        onClick={() => handleDownloadResume('pdf')}
      >
        Download resume (PDF)
      </Button>
    );

  const renderFooter = () => {
    switch (stage) {
      case 'evaluate':
        // Decisions only: the three answers to "what do I do with this
        // opportunity?". Closing lives on the X/Esc/mask; the posting link
        // lives in the header with the rest of the job's reference links.
        return (
          <div className={css.footer}>
            <Button danger onClick={() => setRejectModalOpen(true)} disabled={accepting}>
              Reject
            </Button>
            <div className={css.footerActions}>
              <Button
                onClick={() => {
                  captureEvent('linkedin_stage_opened', { opportunity_id: opportunity.id });
                  setStage('linkedin');
                }}
              >
                Apply through LinkedIn
              </Button>
              <Button
                type="primary"
                onClick={() => setStage('prepare')}
                // No letter to write while there is nobody to send it to: a
                // suggestion awaits a decision, or nobody is reachable. The
                // status panel above says why and offers what's left.
                disabled={!opportunity.template || writeBlocked}
              >
                Apply
              </Button>
            </div>
          </div>
        );
      case 'linkedin':
        // Same close semantics as evaluate: nothing here is unsaved, so the
        // X/Esc/mask close directly (handleClose resets the stage) — no gates.
        return (
          <div className={css.footer}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setStage('evaluate')}>
              Back
            </Button>
            <Button
              type="primary"
              onClick={openDeclare}
            >
              I just applied
            </Button>
          </div>
        );
      case 'prepare':
        return (
          <div className={css.footer}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setStage('evaluate')}>
              Back
            </Button>
            <div className={css.footerActions}>
              <Button onClick={handleReset}>Discard my edits</Button>
              <Button
                type="primary"
                onClick={() => setStage('review')}
                disabled={!subject.trim() || !body.trim()}
              >
                Review
              </Button>
            </div>
          </div>
        );
      case 'review':
        return (
          <div className={css.footer}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setStage('prepare')} disabled={loading}>
              Back
            </Button>
            {!gmailBlocked && (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={gmailConnected === null || sendBlocked}
                icon={<SendOutlined />}
              >
                Confirm & Send
              </Button>
            )}
          </div>
        );
    }
  };

  return (
    <Drawer
      title={null}
      placement="right"
      // Responsive: breathe on desktop, never overflow small screens.
      width="min(720px, 94vw)"
      onClose={handleClose}
      open={open}
      className={css.opportunityDrawer}
      // The header is otherwise a lone close button, so there is room for the
      // full phrase — and unlike the row control, which has to fit a 44px
      // column, this one can spend it. It sits outside the stage switch on
      // purpose: "come back to this" is worth reaching for from the cover-letter
      // editor, not only from the first screen.
      //
      // Rendered only when the parent owns the toggle; without a handler this
      // would be a control that silently does nothing.
      extra={
        onToggleSaved && (
          <Button
            // aria-hidden: @ant-design/icons puts `aria-label="book"` on every
            // icon span, and this button's accessible name is computed from its
            // contents — unhidden it announces as "book Save for later".
            icon={saved ? <BookmarkFilled aria-hidden /> : <BookmarkOutlined aria-hidden />}
            onClick={() => onToggleSaved?.(opportunity)}
            // Matches the row control. Blocking the click visibly beats taking
            // it and discarding it: an immediate undo of a save would otherwise
            // look accepted and leave the row saved.
            disabled={savePending}
          >
            {/* "for later" is load-bearing, not padding. From the prepare and
                review stages this button sits above a subject/body/footer editor
                and beside Confirm & Send, where a bare "Save" reads as "save my
                draft" — and a user who believes they saved edits they didn't is
                worse off than one who never clicked. Same words as the row
                control's tooltip, so the feature speaks with one vocabulary.
                No tooltip here: the visible label already says it, and hover-only
                help would repeat it to everyone except keyboard users.

                The name carries the state, as it does on the row control and the
                Saved filter; deliberately no aria-pressed. */}
            {saved ? 'Saved for later' : 'Save for later'}
          </Button>
        )
      }
      footer={renderFooter()}
    >
      {/* Always mounted, outside the stages: see `announcement`. */}
      <div className={css.srOnly} role="status" aria-live="polite" data-testid="contact-status-live">
        <span key={announcement.key}>{announcement.text}</span>
      </div>

      {/* Stage 1: Evaluate */}
      {stage === 'evaluate' && (
        <div className={css.evaluateStage}>
          {/* Header */}
          <div className={css.header}>
            <div className={css.titleRow}>
              {/* tabIndex -1: focus lands here after "Use this contact" on this
                  stage, when the panel that held the button goes away. */}
              <Title level={4} className={css.title} ref={headingRef} tabIndex={-1}>
                {opportunity.title}
              </Title>
              <Tag color={rating.color}>{rating.label}</Tag>
            </div>
            <Text className={css.company}>@ {opportunity.company.name}</Text>
            <SourceTag
              source={opportunity.source}
              sourceSite={opportunity.sourceSite}
              className={css.sourceTag}
            />
            {opportunity.summary && (
              <div className={css.meta}>
                {opportunity.summary.jobType && (
                  <span className={css.metaItem}>{opportunity.summary.jobType}</span>
                )}
                {opportunity.summary.salaryRange && (
                  <span className={css.metaItem}>{opportunity.summary.salaryRange}</span>
                )}
                {opportunity.summary.experienceLevel && (
                  <span className={css.metaItem}>{opportunity.summary.experienceLevel}</span>
                )}
              </div>
            )}
            <div className={css.headerLinks}>
              <a
                href={opportunity.jobPostingUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View the job posting — opens in a new tab"
              >
                <ExportOutlined /> View posting
              </a>
            </div>
            {age.valid && !age.isStale && (
              <div className={css.postedLine}>
                Posted {age.absolute} · {age.relative}
              </div>
            )}
            {age.valid && age.isStale && (
              <div className={css.staleNotice}>
                <ClockCircleOutlined className={css.staleIcon} />
                <div>
                  <Text strong>Posted {age.relative}.</Text>
                  <br />
                  Older ads often close on their own — which doesn't mean the role was filled.
                  {/* Not while there's no confirmed contact: the status panel
                      says what's possible instead. */}
                  {!writeBlocked && (
                    <>
                      <br />
                      The contact is still worth reaching out to.
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {renderNotice()}
          {renderContactStatus()}

          {/* "Contacting a real person beats applying online" would argue
              with the panel above when there is nobody to contact. */}
          {!reassuranceDismissed && contactStatus !== 'unreachable' && renderVolumeReassurance()}

          <Divider />

          {/* Why You're a Good Fit */}
          <section className={css.section}>
            <Title level={5}>Why You're a Good Fit</Title>
            {renderBulletList(opportunity.suitabilityReason)}
          </section>

          {eligibilityNote && (
            <Alert
              type={eligibilityNote === 'likely' ? 'info' : 'warning'}
              showIcon
              className={css.eligibilityNote}
              message={
                eligibilityNote === 'likely'
                  ? `The posting hires across a region that should include you${
                      residence ? ` (${residence})` : ''
                    }, but it doesn't name your country — confirm before applying.`
                  : residence
                    ? `The posting doesn't say whether it hires from ${residence}. Check before applying.`
                    : "The posting doesn't say where it hires from. Check before applying."
              }
              description={eligibilityQuote ? `“${eligibilityQuote}”` : undefined}
            />
          )}

          {/* Job Summary */}
          {opportunity.summary && (
            <>
              <Divider />
              <section className={css.section}>
                <Title level={5}>Job Summary</Title>
                <Paragraph>{opportunity.summary.generalOverview}</Paragraph>

                {opportunity.summary.keyResponsibilities && (
                  <div className={css.listSection}>
                    <Text strong>Key Responsibilities:</Text>
                    <ul>
                      {opportunity.summary.keyResponsibilities.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {opportunity.summary.requiredQualifications && (
                  <div className={css.listSection}>
                    <Text strong>Required Qualifications:</Text>
                    <ul>
                      {opportunity.summary.requiredQualifications.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            </>
          )}

          {/* Company Info */}
          <Divider />
          <section className={css.section}>
            <Title level={5}>
              About {opportunity.company.name}
              {opportunity.company.summary?.industry && (
                <Text type="secondary" className={css.aboutIndustry}>
                  {' '}· {opportunity.company.summary.industry}
                </Text>
              )}
            </Title>
            <Paragraph>{opportunity.company.description}</Paragraph>
            <div className={css.links}>
              {opportunity.company.websiteUrl && (
                <a href={opportunity.company.websiteUrl} target="_blank" rel="noopener noreferrer">
                  <GlobalOutlined /> Website
                </a>
              )}
              {opportunity.company.linkedinUrl && (
                <a href={opportunity.company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                  <LinkedinOutlined /> LinkedIn
                </a>
              )}
            </div>
          </section>

          {/* One-line teaser where the full WarmPathNudge used to live — the
              coaching itself moved into the Apply-through-LinkedIn stage. */}
          <Divider />
          <Text type="secondary" className={css.warmTeaser}>
            Know someone at {opportunity.company.name}? Warm intros beat everything — try
            "Apply through LinkedIn".
          </Text>

          {/* Independent-apply escape hatch: the exact resume outreach would
              attach, downloadable for applying outside Beast. Lives here at
              the end of the evaluate body — the footer stays decisions-only. */}
          <Divider />
          <section className={css.downloadResumeRow}>
            <Text type="secondary" className={css.downloadResumeHint}>
              Applying on your own? Use the same resume we send.
            </Text>
            {renderResumeDownloadButton()}
          </section>
        </div>
      )}

      {/* Stage 1b: Apply through LinkedIn — the warm-intro workspace */}
      {stage === 'linkedin' && (
        <div className={css.linkedinStage}>
          {opportunity.contact && (
            <>
              <Text type="secondary" className={css.introLine}>
                If someone can intro you, this is the person they should intro you <em>to</em>.
              </Text>
              <section className={css.section}>
                <Title level={5}>Contact</Title>
                <ContactCard
                  contact={opportunity.contact}
                  explanation={opportunity.contactExplanation}
                  selectionReasoning={opportunity.contactSelectionReasoning}
                />
              </section>
              <Divider />
            </>
          )}

          {/* Warm-path actions + coaching: the relocated nudge carries the
              "see who you know" / "draft an intro request" CTAs and the stat.
              prominent: here the nudge IS the stage, so its starting action
              gets primary weight. */}
          <WarmPathNudge
            prominent
            companyName={opportunity.company.name}
            linkedinConnectionsUrl={opportunity.linkedinConnectionsUrl}
            onSeeWhoClick={() =>
              captureEvent('linkedin_connections_clicked', { opportunity_id: opportunity.id })
            }
            onDraftIntro={() => {
              captureEvent('intro_request_opened', { opportunity_id: opportunity.id });
              setIntroModalOpen(true);
            }}
          />

          <Divider />
          <section className={css.downloadResumeRow}>
            <Text type="secondary" className={css.downloadResumeHint}>
              Sharing your resume with a connection? Use the same one we send.
            </Text>
            {renderResumeDownloadButton()}
          </section>

          <Divider />
          {/* With no confirmed contact there is no email to go back to (the
              evaluate stage's status panel offers what's left), so only the
              referral half of the advice stands. */}
          <Text type="secondary" className={css.returnPath}>
            {writeBlocked ? (
              <>
                Don't know anyone there? That's normal. If someone will genuinely vouch for you,
                that referral can be up to ~13× more likely to land an interview.
              </>
            ) : (
              <>
                Don't know anyone there? That's normal — go back and Apply. A specific, personal
                email{opportunity.contact ? ` to ${opportunity.contact.firstName}` : ''} can still
                outperform the apply button — and if someone will genuinely vouch for you, that
                referral can be up to ~13× more likely to land an interview.
              </>
            )}
          </Text>
        </div>
      )}

      {/* Stage 2: Prepare */}
      {stage === 'prepare' && (
        <div className={css.prepareStage}>
          <Steps
            current={0}
            size="small"
            className={css.steps}
            items={[
              { title: 'Prepare', icon: <EditOutlined /> },
              { title: 'Review & Send', icon: <SendOutlined /> },
            ]}
          />

          {renderNotice()}

          {renderContactStatus()}

          {/* Gmail connection prompt — early visibility */}
          {gmailBlocked && renderGmailAlert('info')}

          {/* Contact Card */}
          <section className={css.section}>
            <Title level={5}>Contact</Title>
            <ContactCard
              contact={opportunity.contact}
              explanation={opportunity.contactExplanation}
              selectionReasoning={opportunity.contactSelectionReasoning}
            />
          </section>

          <Divider />

          {/* Editable Cover Letter */}
          <section className={css.section}>
            <div className={css.sectionHeader}>
              <Title level={5}>Cover Letter</Title>
              {template && (
                <DraftLanguageChip
                  language={template.language}
                  languageName={template.languageName}
                  reason={template.reason}
                />
              )}
            </div>

            <div className={css.regenerateRow}>
              <span className={css.regenerateLabel}>Regenerate in</span>
              <LanguagePicker
                value={regenLanguage}
                onChange={setRegenLanguage}
                size="small"
                disabled={regenerating}
                ariaLabel="Regenerate in language"
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={regenerating}
                onClick={handleRegenerate}
              >
                Regenerate
              </Button>
            </div>

            <div className={css.field}>
              <label>Subject</label>
              <Input
                ref={subjectInputRef}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
                dir="auto"
              />
            </div>
            <div className={css.field}>
              <label>Message</label>
              <TextArea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Your cover letter..."
                rows={10}
                className={css.bodyInput}
                dir="auto"
              />
            </div>
            <div className={css.field}>
              <label>Signature / Footer</label>
              <TextArea
                value={footer}
                onChange={(e) => setFooter(e.target.value)}
                placeholder="Your signature..."
                rows={3}
                className={css.footerInput}
                dir="auto"
              />
            </div>
          </section>
        </div>
      )}

      {/* Stage 3: Review & Send */}
      {stage === 'review' && (
        <div className={css.reviewStage}>
          <Steps
            current={1}
            size="small"
            className={css.steps}
            items={[
              { title: 'Prepare', icon: <EditOutlined /> },
              { title: 'Review & Send', icon: <SendOutlined /> },
            ]}
          />

          {renderNotice()}
          {renderContactStatus()}

          {gmailBlocked && (
            <ConnectGmailStep
              recipientEmail={opportunity.contact?.email ?? ''}
              variant={gmailNoSend ? 'fix' : 'connect'}
              onConnect={() => openPrimer(gmailNoSend ? 'fix' : 'connect')}
            />
          )}

          <details className={css.previewDetails} open={!gmailBlocked}>
            <summary className={css.previewSummary}>
              <span>Your message</span>
              <DownOutlined className={css.previewChevron} />
            </summary>
            <div className={css.emailPreview}>
              <div className={css.previewHeader}>
                <div className={css.previewRow}>
                  <Text strong>To:</Text>
                  <Text>{opportunity.contact?.email}</Text>
                </div>
                <div className={css.previewRow}>
                  <Text strong>Subject:</Text>
                  <Text dir="auto">{subject}</Text>
                </div>
              </div>

              <div className={css.previewBody}>
                <Paragraph className={css.previewText} dir="auto">{body}</Paragraph>
                {footer && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Paragraph className={css.previewText} type="secondary" dir="auto">{footer}</Paragraph>
                  </>
                )}
              </div>

              <div className={css.attachment}>
                <PaperClipOutlined />
                <Text type="secondary">{resumeAttachmentName} (attached)</Text>
              </div>
            </div>
          </details>
        </div>
      )}

      <RejectModal
        subjects={[
          { id: opportunity.id, title: opportunity.title, company: opportunity.company.name },
        ]}
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSuccess={() => {
          setRejectModalOpen(false);
          onReject();
        }}
        source="drawer"
      />

      {/* key (both modals): a different opportunity must not inherit the previous
          draft state — the opportunity-change effect force-closes them without
          their own close handlers running, so stale form state could otherwise
          survive into the next opportunity. Prefixes keep the sibling keys
          unique (duplicate keys are unsupported by React). */}
      <DeclareApplicationModal
        key={`declare-${opportunity.id}`}
        opportunityId={opportunity.id}
        opportunityTitle={opportunity.title}
        open={declareModalOpen}
        onClose={() => setDeclareModalOpen(false)}
        onSuccess={() => {
          setDeclareModalOpen(false);
          handleClose();
          onApply(); // same post-apply path as approve: close + refresh pending list
        }}
      />

      <IntroRequestModal
        key={`intro-${opportunity.id}`}
        opportunityId={opportunity.id}
        companyName={opportunity.company.name}
        open={introModalOpen}
        onClose={() => setIntroModalOpen(false)}
      />

      <ReplyCaptureModal
        open={replyCaptureModal.open}
        variant={replyCaptureModal.variant}
        recipientFirstName={opportunity.contact?.firstName}
        applicationCount={replyCaptureModal.count}
        onInstallAddon={handleReplyCaptureInstall}
        onSetupForward={handleReplyCaptureForward}
        onLater={closeReplyCaptureModal}
        onClose={closeReplyCaptureModal}
      />

      {primer}
    </Drawer>
  );
};

export default OpportunityDrawer;
